//
// Copyright 2025 DXOS.org
//

import * as Schema from 'effect/Schema';

import { Capability } from '@dxos/app-framework';
import { Operation } from '@dxos/compute';
import { Identity } from '@dxos/halo';
import { DXN, IdentityDid, SpaceId } from '@dxos/keys';

import { meta } from '#meta';

const makeKey = (name: string) => DXN.make(`${meta.profile.key}.operation.${name}`);

const IdentitySchema = Schema.Struct({
  identityDid: IdentityDid,
  spaceId: Schema.optional(SpaceId),
  profile: Schema.optional(
    Schema.Struct({
      displayName: Schema.optional(Schema.String),
      avatarCid: Schema.optional(Schema.String),
      data: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.Any })),
    }),
  ),
});

const ProfileSchema = Schema.Struct({
  displayName: Schema.optional(Schema.String),
  avatarCid: Schema.optional(Schema.String),
  data: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.Any })),
});

export const CreateIdentity = Operation.make({
  meta: { key: makeKey('createIdentity'), name: 'Create Identity', icon: 'ph--user--regular' },
  services: [Capability.Service, Identity.Service],
  input: ProfileSchema,
  output: IdentitySchema,
});

export const UpdateProfile = Operation.make({
  meta: { key: makeKey('updateProfile'), name: 'Update Profile', icon: 'ph--user--regular' },
  services: [Identity.Service],
  input: ProfileSchema,
  output: Schema.Void,
});

export const JoinIdentity = Operation.make({
  meta: { key: makeKey('joinIdentity'), name: 'Join Identity', icon: 'ph--sign-in--regular' },
  services: [Capability.Service],
  input: Schema.Struct({
    invitationCode: Schema.optional(Schema.String),
  }),
  output: Schema.Void,
});

export const ShareIdentity = Operation.make({
  meta: {
    key: makeKey('shareIdentity'),
    name: 'Share Identity',
    icon: 'ph--share-network--regular',
  },
  services: [Capability.Service],
  input: Schema.Void,
  output: Schema.Void,
});

export const OpenUsage = Operation.make({
  meta: {
    key: makeKey('openUsage'),
    name: 'Open Usage',
    icon: 'ph--chart-bar--regular',
  },
  services: [Capability.Service],
  input: Schema.Void,
  output: Schema.Void,
});

export const RecoverIdentity = Operation.make({
  meta: { key: makeKey('recoverIdentity'), name: 'Recover Identity', icon: 'ph--key--regular' },
  services: [Capability.Service],
  input: Schema.Void,
  output: Schema.Void,
});

export const ResetStorage = Operation.make({
  meta: { key: makeKey('resetStorage'), name: 'Reset Storage', icon: 'ph--warning--regular' },
  services: [Capability.Service],
  input: Schema.Struct({
    mode: Schema.optional(Schema.String),
  }),
  output: Schema.Void,
});

export const CreateAgent = Operation.make({
  meta: { key: makeKey('createAgent'), name: 'Create Agent', icon: 'ph--brain--regular' },
  services: [Capability.Service],
  input: Schema.Void,
  output: Schema.Void,
});

export const CreateRecoveryCode = Operation.make({
  meta: {
    key: makeKey('createRecoveryCode'),
    name: 'Create Recovery Code',
    icon: 'ph--key--regular',
  },
  services: [Capability.Service],
  input: Schema.Void,
  output: Schema.Void,
});

export const CreatePasskey = Operation.make({
  meta: { key: makeKey('createPasskey'), name: 'Create Passkey', icon: 'ph--key--regular' },
  services: [Capability.Service],
  input: Schema.Void,
  output: Schema.Void,
});

export const RedeemPasskey = Operation.make({
  meta: { key: makeKey('redeemPasskey'), name: 'Redeem Passkey', icon: 'ph--key--regular' },
  services: [Capability.Service],
  input: Schema.Void,
  output: Schema.Void,
});

export const RedeemToken = Operation.make({
  meta: { key: makeKey('redeemToken'), name: 'Redeem Token', icon: 'ph--lock--regular' },
  services: [Capability.Service],
  input: Schema.Struct({
    token: Schema.String,
  }),
  output: Schema.Void,
});
